import { copilotStudioConversationUrl } from "./copilotStudioConnection"

export type CopilotCitation = {
  title: string
  url?: string
}

export type CopilotActivity = {
  type?: string
  text?: string
  speak?: string
  id?: string
  name?: string
  conversation?: { id?: string }
  channelData?: { streamType?: string; streamId?: string }
  entities?: Array<{
    type?: string
    streamType?: string
    streamId?: string
    citation?: Array<{
      title?: string
      url?: string
      appearance?: { name?: string; title?: string; url?: string }
    }>
  }>
}

export function citationsFromActivity(activity: CopilotActivity): CopilotCitation[] {
  const citations: CopilotCitation[] = []
  activity.entities?.forEach((entity) => {
    entity.citation?.forEach((item) => {
      const title =
        item.appearance?.name ||
        item.appearance?.title ||
        item.title ||
        ""
      const url = item.appearance?.url || item.url
      if (!title && !url) return
      citations.push({ title: title || url || "Source", url })
    })
  })
  return citations
}

type ConnectionOptions = {
  environmentId: string
  schemaName: string
  directConnectUrl?: string
  token: string
}

function activityText(activity: CopilotActivity) {
  return (activity.text || "").trim()
}

export function agentRepliesFromActivities(activities: CopilotActivity[]) {
  const streams = new Map<string, { text: string; citations: CopilotCitation[] }>()
  const done: { text: string; citations: CopilotCitation[] }[] = []
  activities.forEach((activity) => {
    if ((activity.type || "").toLowerCase() !== "message") return
    const text = (activity.text || activity.speak || "").trim()
    if (!text) return
    const info = activity.entities?.find((entity) => entity.type === "streaminfo")
    const streamType = activity.channelData?.streamType || info?.streamType
    const streamId = activity.channelData?.streamId || info?.streamId || "default"
    if (streamType === "streaming") {
      const previous = streams.get(streamId)?.text || ""
      const combined = text.startsWith(previous) ? text : `${previous}${text}`
      streams.set(streamId, {
        text: combined,
        citations: citationsFromActivity(activity),
      })
      return
    }
    streams.delete(streamId)
    done.push({ text, citations: citationsFromActivity(activity) })
  })
  if (done.length) return done
  return [...streams.values()]
}

export function isFinalAgentMessage(activity: CopilotActivity) {
  if (activity.type !== "message") return false
  const streamType =
    activity.channelData?.streamType ||
    activity.entities?.find((entity) => entity.type === "streaminfo")?.streamType
  if (streamType === "streaming") return false
  return Boolean(activityText(activity))
}

function activityFromFrame(chunk: string): CopilotActivity | null {
  const lines = chunk.replace(/\r/g, "").split("\n")
  const eventLine = lines.find((line) => line.startsWith("event:"))
  const eventName = eventLine?.slice(6).trim()
  if (eventName && eventName !== "activity") return null
  const data = lines
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim())
    .join("\n")
  const payload = data || chunk.replace(/\r/g, "").trim()
  if (!payload.startsWith("{") && !payload.startsWith("[")) return null
  try {
    const parsed = JSON.parse(payload) as CopilotActivity | CopilotActivity[]
    if (Array.isArray(parsed)) return parsed.find((item) => item.text) || parsed[0] || null
    return parsed
  } catch {
    return null
  }
}

async function readActivities(response: Response): Promise<CopilotActivity[]> {
  const reader = response.body?.getReader()
  if (!reader) return []
  const decoder = new TextDecoder()
  let buffer = ""
  const activities: CopilotActivity[] = []
  let finished = false
  while (!finished) {
    // Streamed responses arrive in chunks; each chunk has to be read before the next.
    // eslint-disable-next-line no-await-in-loop
    const next = await reader.read()
    finished = next.done
    if (!next.value) {
      // no bytes in this read
    } else {
      buffer += decoder.decode(next.value, { stream: !finished }).replace(/\r/g, "")
      const chunks = buffer.split("\n\n")
      buffer = chunks.pop() ?? ""
      chunks.forEach((chunk) => {
        const activity = activityFromFrame(chunk)
        if (activity) activities.push(activity)
      })
    }
  }
  const tail = activityFromFrame(buffer)
  if (tail) activities.push(tail)
  return activities
}

async function postCopilot(
  url: string,
  token: string,
  body: unknown
): Promise<{ conversationId: string; activities: CopilotActivity[] }> {
  let response: Response
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
    })
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? `Could not reach Copilot Studio (${error.message}). Confirm the Entra app allows ${window.location.origin} and the agent is published.`
        : "Could not reach Copilot Studio."
    )
  }

  if (!response.ok) {
    const detail = (await response.text()).slice(0, 400)
    throw new Error(
      `Copilot Studio returned ${response.status}${detail ? `: ${detail}` : ""}`
    )
  }

  const activities = await readActivities(response)
  const fromHeader = response.headers.get("x-ms-conversationid") || ""
  const fromActivity =
    activities.find((activity) => activity.conversation?.id)?.conversation?.id ||
    ""
  return {
    conversationId: fromHeader || fromActivity,
    activities,
  }
}

export async function startCopilotConversation(options: ConnectionOptions) {
  const url = copilotStudioConversationUrl(options)
  return postCopilot(url, options.token, { emitStartConversationEvent: true })
}

export async function sendCopilotMessage(
  options: ConnectionOptions & {
    conversationId: string
    text: string
    attachment?: { name: string; contentType: string; contentUrl: string }
    value?: Record<string, unknown>
  }
) {
  const url = copilotStudioConversationUrl({
    ...options,
    conversationId: options.conversationId,
  })
  const activity: Record<string, unknown> = {
    type: "message",
    text: options.text,
    value: options.value,
  }
  if (options.attachment) {
    activity.attachments = [
      {
        contentType: options.attachment.contentType,
        contentUrl: options.attachment.contentUrl,
        name: options.attachment.name,
      },
    ]
  }
  return postCopilot(url, options.token, {
    activity,
    conversationId: options.conversationId,
  })
}
